import Alert from '@mui/material/Alert';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Loader, SectionTitle } from 'modules/shared/components';
import { logError } from 'utils/logger';
import {
  DocumentCollectionView,
  PersonalDetailsEdit,
  PersonalDetailsView,
  ProfessionalExperienceEdit,
  ProfessionalExperienceView,
} from '../components';
import { DATA_ENTRY_FORM_DEFAULTS, DATA_ENTRY_MESSAGES, PERSONAL_DATA_STATUS } from '../constants';
import { getOfferContext, submitCandidateDataEntry } from '../interview.public.service';

const DATA_ENTRY_UI_MODE = {
  LOADING: 'loading',
  BLOCKED: 'blocked',
  VIEW: 'view',
  EDIT: 'edit',
};

function normalizePersonalDataToForm(personalData: Record<string, any> = {}) {
  const { documents = [], professionalExperience = [], ...rest } = personalData;

  return {
    personalData: {
      ...rest,
      dateOfBirth: rest.dateOfBirth || null,
    },
    documents: documents.reduce((acc, doc) => {
      acc[doc.name] = { backendFile: doc };
      return acc;
    }, {}),
    professionalExperience,
    newDoc: {},
  };
}

function resolveUiMode({ loading, status }) {
  if (loading) return DATA_ENTRY_UI_MODE.LOADING;

  if (status !== PERSONAL_DATA_STATUS.SHARED && status !== PERSONAL_DATA_STATUS.SUBMITTED) {
    return DATA_ENTRY_UI_MODE.BLOCKED;
  }

  if (status === PERSONAL_DATA_STATUS.SUBMITTED) {
    return DATA_ENTRY_UI_MODE.VIEW;
  }

  return DATA_ENTRY_UI_MODE.EDIT;
}

export default function CandidateDataEntry() {
  const { applicationId } = useParams();

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [offerData, setOfferData] = useState(null);
  const [isExperienceEditing, setIsExperienceEditing] = useState(false);

  const methods = useForm({
    defaultValues: DATA_ENTRY_FORM_DEFAULTS,
  });
  const { reset, handleSubmit } = methods;

  const fetchOfferContext = useCallback(async () => {
    if (!applicationId) return;

    setLoading(true);
    try {
      const data = await getOfferContext(applicationId);
      console.log(data);
      setOfferData(data);
    } catch (error) {
      toast.error(
        logError({
          error,
          message: DATA_ENTRY_MESSAGES.FETCH_FAILED,
          code: 'CANDIDATE_DETAILS_FETCH_FAILED',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchOfferContext();
  }, [fetchOfferContext]);

  useEffect(() => {
    if (!offerData?.personalData) return;

    reset(normalizePersonalDataToForm(offerData.personalData), {
      keepDirty: true,
    });
  }, [offerData, reset]);

  const uiMode = useMemo(
    () =>
      resolveUiMode({
        loading,
        status: offerData?.personalData?.status,
      }),
    [loading, offerData],
  );

  const handleFormSubmit = async (formData) => {
    if (isExperienceEditing) {
      toast.error(DATA_ENTRY_MESSAGES.EXPERIENCE_EDIT_BLOCK);
      return;
    }

    setUpdating(true);
    try {
      await submitCandidateDataEntry(applicationId, formData);
      toast.success(DATA_ENTRY_MESSAGES.SUBMIT_SUCCESS);
      fetchOfferContext();
    } catch (error) {
      toast.error(
        logError({
          error,
          message: DATA_ENTRY_MESSAGES.SUBMIT_FAILED,
          code: 'CANDIDATE_DATA_ENTRY_SUBMIT_FAILED',
        }),
      );
    } finally {
      setUpdating(false);
    }
  };

  console.log(offerData);

  return (
    <div className='column gap-5'>
      <SectionTitle title='ON-BOARDING PROCESS - PERSONAL DATA ENTRY TO OUR BOARD' />

      {uiMode === DATA_ENTRY_UI_MODE.LOADING && <Loader />}

      {uiMode === DATA_ENTRY_UI_MODE.BLOCKED && (
        <Alert severity='info'>{DATA_ENTRY_MESSAGES.FORM_NOT_SHARED}</Alert>
      )}

      {uiMode === DATA_ENTRY_UI_MODE.VIEW && (
        <div className='column gap-8'>
          <PersonalDetailsView data={offerData?.personalData || {}} />
          <DocumentCollectionView data={offerData?.personalData?.documents || []} />
          <ProfessionalExperienceView
            data={offerData?.personalData?.professionalExperience || []}
          />
        </div>
      )}

      {uiMode === DATA_ENTRY_UI_MODE.EDIT && (
        <FormProvider {...methods}>
          <form
            id='candidate-data-entry-form'
            className='column gap-8'
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <PersonalDetailsEdit />
            <DocumentCollectionView />
            <ProfessionalExperienceEdit
              initialSavedCount={offerData?.personalData?.professionalExperience?.length || 0}
              onEditStateChange={setIsExperienceEditing}
            />

            <button
              form='candidate-data-entry-form'
              type='submit'
              className='btn cta-btn self-end'
              disabled={updating}
            >
              SUBMIT
            </button>
          </form>
        </FormProvider>
      )}
    </div>
  );
}
